import { Amplify } from 'aws-amplify';
import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  AmplifyConfigBranch,
  AmplifyConfigDev,
  AmplifyConfigProd,
  AmplifyConfigStaging,
  AmplifyConfigType,
} from '../config/AppConfig';
import { useOktaAuth } from '@okta/okta-react';
import { apiClient } from '../services/ApiClient';

const envMap = {
  branch: AmplifyConfigBranch,
  dev: AmplifyConfigDev,
  staging: AmplifyConfigStaging,
  prod: AmplifyConfigProd,
};

export type Env = 'branch' | 'dev' | 'staging' | 'prod';

type EnvContextType = {
  appsyncUrl: string;
  appsyncWssUrl: string;
  appsyncRegion: string;
  local: boolean;
  setEnv: React.Dispatch<React.SetStateAction<Env>>;
  env: Env;
  baseUrl: string;
};

export const EnvContext = createContext({
  appsyncUrl: '',
  appsyncRegion: 'us-east-1',
  appsyncWssUrl: '',
  local: false,
  setEnv: () => undefined,
  env: 'staging',
  baseUrl: '',
} as EnvContextType);

export const EnvProvider = ({ children }: { children: React.ReactNode }) => {
  const { authState } = useOktaAuth();
  const token = authState?.accessToken?.accessToken.toString() ?? 'NO TOKEN';

  const [env, setEnv] = useState<Env>(
    (localStorage.getItem('app-env') as Env) || 'dev',
  );
  const [config, setConfig] = useState<any>();
  const [baseUrl, setBaseUrl] = useState<string>('');

  useEffect(() => {
    const configureAmplify = (amplifyConfig: AmplifyConfigType): void => {
      Amplify.configure(amplifyConfig);
      setConfig(amplifyConfig);
    };
    configureAmplify(envMap[env]);
    setBaseUrl(envMap[env].restApiEndpoint);
    apiClient.setBaseURL(envMap[env].restApiEndpoint);
  }, [env]);

  useEffect(() => {
    if (token && token !== 'NO TOKEN') {
      apiClient.setToken(token);
    }
  }, [token]);

  const local = window.location.href.includes('localhost');
  return (
    <EnvContext.Provider
      value={{
        appsyncRegion: config?.aws_appsync_region ?? 'us-east-1',
        appsyncUrl: config?.aws_appsync_graphqlEndpoint ?? '',
        appsyncWssUrl: '',
        local,
        setEnv,
        env,
        baseUrl,
      }}
    >
      {/* Note, if we render the children without a config being set, we run the risk of a RequireAuth forcibly logging us out */}
      {config ? children : null}
    </EnvContext.Provider>
  );
};
export function useEnv() {
  return useContext(EnvContext);
}
